import { PageHero, Clause } from "@/components/site/PageParts";
import { Reveal } from "@/lib/motion";
import { ShieldWarning, MapPinLine, ChatCircleDots, Prohibit } from "@phosphor-icons/react";

const TIPS = [
  { icon: ChatCircleDots, t: "Take your time", d: "Chat first. Never feel rushed into meeting or sharing personal details before you're ready." },
  { icon: MapPinLine, t: "Meet in public", d: "For first meetings, choose a public place, tell a friend where you'll be, and arrange your own transport." },
  { icon: ShieldWarning, t: "Protect your privacy", d: "Keep financial details, home address, and identifying info private until you genuinely trust someone." },
  { icon: Prohibit, t: "Trust your instincts", d: "If something feels off, walk away. Block and report anyone who pressures, threatens, or deceives you." },
];

export default function Safety() {
  return (
    <div data-testid="safety-page">
      <PageHero
        index="C/"
        overline="Safety Guidelines"
        titleLines={["Meet smart.", <>Stay <span className="italic text-oxblood">safe.</span></>]}
        intro="Practical guidance to help you protect yourself while connecting with other adults."
      />

      <section className="mx-auto max-w-[1400px] px-6 md:px-12 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-14">
          {TIPS.map((tip, i) => (
            <Reveal key={i} delay={i * 0.07}>
              <div className="border-t border-ink pt-6">
                <tip.icon size={34} weight="light" className="text-oxblood mb-5" />
                <h3 className="font-serif text-2xl sm:text-3xl mb-3">{tip.t}</h3>
                <p className="text-ink-soft leading-relaxed text-lg">{tip.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-ink text-paper">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-20 md:py-28">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-oxblood mb-6">
              Zero tolerance
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02] max-w-4xl">
              These are strictly <span className="italic text-oxblood">prohibited</span> and actively removed.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-8">
            {[
              "Any illegal content or activity",
              "Paid sexual services or solicitation",
              "Human trafficking or exploitation",
              "Any involvement of minors (under 18)",
              "Harassment, threats, or stalking",
              "Non-consensual content or coercion",
              "Impersonation or deceptive behaviour",
              "Sharing others' private information",
            ].map((item, i) => (
              <Reveal key={i} delay={i * 0.04}>
                <div className="flex items-start gap-4 border-t border-paper/15 pt-4">
                  <span className="font-serif text-xl text-oxblood leading-none">✕</span>
                  <span className="text-paper/80 text-lg">{item}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[820px] px-6 md:px-12 py-20 md:py-24 space-y-12">
        <Clause number="§1" title="Reporting a concern">
          <p>
            If you encounter content or behaviour that breaks our rules or puts
            anyone at risk, report it immediately. Use in-platform reporting where
            available, or email us at <a href="mailto:space@interlacedsociial.com" className="text-oxblood underline">space@interlacedsociial.com</a>.
            Reports are reviewed and acted on promptly.
          </p>
        </Clause>
        <Clause number="§2" title="In an emergency">
          <p>
            If you or someone else is in immediate danger, contact your local
            emergency services first. Doublelist is not a substitute for law
            enforcement, and we cooperate fully with lawful investigations.
          </p>
        </Clause>
      </section>
    </div>
  );
}
